import useAuth from '@hooks/use-auth';
import { useIntersectionObserver, useOnIntersect } from '@hooks/use-intersection-observer';
import useLastCallback from '@hooks/use-last-callback';
import { debounce } from '@utils/schedulers';
import type { RefObject } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useBoundStore } from '@/newstore';
import { useMessages } from '@/newstore/selectors/messages';

export type LoadMoreDirection = 'backwards' | 'forwards';

const FAB_THRESHOLD = 50;
const NOTCH_THRESHOLD = 1;

function useScroll(
  conversationId: number,
  containerRef: RefObject<HTMLDivElement>,
  hasUnread: boolean,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onFabToggle: (...args: any[]) => void,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onNotchToggle: (...args: any[]) => void,
) {
  const { messageIds } = useMessages(conversationId);
  const loadMessages = useBoundStore(useShallow((state) => state.loadConversationMessages));
  const isReady = Boolean(messageIds);
  const auth = useAuth();
  const [loadMoreBackwards, loadMoreForwards] = useMemo(
    () => [
      debounce(() => loadMessages(auth, conversationId, 'before'), 1000, true, false),
      debounce(() => loadMessages(auth, conversationId, 'after'), 1000, true, false),
    ],
    [auth, conversationId, loadMessages],
  );

  const backwardsTriggerRef = useRef<HTMLDivElement>(null);
  const forwardsTriggerRef = useRef<HTMLDivElement>(null);
  const fabTriggerRef = useRef<HTMLDivElement>(null);

  const toggleScrollTools = useLastCallback(() => {
    if (!isReady) {
      return;
    }

    if (!messageIds?.length) {
      onFabToggle(false);
      onNotchToggle(false);
      return;
    }

    const container = containerRef.current!;
    if (!container) {
      return;
    }

    const { offsetHeight, scrollHeight, scrollTop } = container;
    const scrollBottom = Math.round(scrollHeight - scrollTop - offsetHeight);
    const isNearBottom = scrollBottom <= FAB_THRESHOLD;
    const isAtBottom = scrollBottom <= NOTCH_THRESHOLD;

    if (scrollHeight === 0) {
      return;
    }

    onFabToggle(hasUnread ? !isAtBottom : !isNearBottom);
    onNotchToggle(!isAtBottom);
  });

  const { observe: observeIntersection } = useIntersectionObserver(
    {
      rootRef: containerRef,
      margin: 200,
    },
    (entries) => {
      if (!loadMoreForwards || !loadMoreBackwards) {
        return;
      }

      const container = containerRef.current!;

      entries.forEach(({ isIntersecting, target }) => {
        if (!isIntersecting) {
          return;
        }

        if (target.className === 'backwards-trigger' && container.scrollHeight > container.offsetHeight) {
          loadMoreBackwards();
        }

        if (target.className === 'forwards-trigger') {
          loadMoreForwards();
        }
      });
    },
  );

  const withHistoryTriggers = messageIds && messageIds.length > 1;

  useOnIntersect(backwardsTriggerRef, observeIntersection);
  useOnIntersect(forwardsTriggerRef, observeIntersection);

  const { observe: observeIntersectionForFab } = useIntersectionObserver(
    {
      rootRef: containerRef,
      margin: FAB_THRESHOLD * 2,
    },
    toggleScrollTools,
  );

  useOnIntersect(fabTriggerRef, observeIntersectionForFab);

  const { observe: observeIntersectionForNotch } = useIntersectionObserver(
    {
      rootRef: containerRef,
      margin: NOTCH_THRESHOLD,
    },
    toggleScrollTools,
  );

  useOnIntersect(fabTriggerRef, observeIntersectionForNotch);

  useEffect(() => {
    if (isReady) {
      toggleScrollTools();
    }
  }, [conversationId, isReady, toggleScrollTools]);

  return { withHistoryTriggers, backwardsTriggerRef, forwardsTriggerRef, fabTriggerRef };
}

export default useScroll;
